import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Image,
  Checkbox,
  Tooltip,
  Timeline,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  RollbackOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Story } from '../../types';
import { storiesApi, tagsApi, CreateStoryRequest, UpdateStoryRequest, usersApi, SchedulePreviewEntry } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, getStatusColor, truncateText } from '../../utils';
import { STORY_STATUS, PAGE_SIZE, GENDER, TAG_STATUS } from '../../constants';
import { ImageUpload } from '../../components/Layout';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const PINNED_STORY_IDS_KEY = 'talebox_admin_stories_pinned_ids';
const MAX_PINNED_STORIES = 50;
const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const MIN_TABLE_PAGE_SIZE = 1;
const MAX_TABLE_PAGE_SIZE = 100;

function readPinnedStoryIds(): string[] {
  try {
    const raw = localStorage.getItem(PINNED_STORY_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function clampTablePageSize(n: number): number {
  if (Number.isNaN(n) || n < MIN_TABLE_PAGE_SIZE) return PAGE_SIZE;
  return Math.min(n, MAX_TABLE_PAGE_SIZE);
}

const Stories: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params or defaults
  const [searchText, setSearchText] = useState<string>(() => searchParams.get('name') || '');
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');
  const [tagFilter, setTagFilter] = useState<string>(() => searchParams.get('categoryId') || '');
  const [createdByFilter, setCreatedByFilter] = useState<string>(() => searchParams.get('createdBy') || '');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(page) || page <= 0 ? 1 : page;
  });
  const [tablePageSize, setTablePageSize] = useState<number>(() =>
    clampTablePageSize(parseInt(searchParams.get('pageSize') || String(PAGE_SIZE), 10))
  );
  const [pinnedIds, setPinnedIds] = useState<string[]>(readPinnedStoryIds);
  const [creatorOptions, setCreatorOptions] = useState<{ label: string; value: string }[]>([]);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);
  const [form] = Form.useForm();
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Schedule preview confirm modal state
  const [schedulePreviewVisible, setSchedulePreviewVisible] = useState(false);
  const [schedulePreviewEntries, setSchedulePreviewEntries] = useState<SchedulePreviewEntry[]>([]);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Update URL params when filters change (but not on initial mount to avoid conflicts)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const params = new URLSearchParams();
    if (searchText) params.set('name', searchText);
    if (statusFilter) params.set('status', statusFilter);
    if (tagFilter) params.set('categoryId', tagFilter);
    if (createdByFilter) params.set('createdBy', createdByFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (tablePageSize !== PAGE_SIZE) params.set('pageSize', String(tablePageSize));

    setSearchParams(params, { replace: true });
  }, [searchText, statusFilter, tagFilter, createdByFilter, currentPage, tablePageSize, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    usersApi
      .getUsers({ pageIndex: 0, pageSize: 500 })
      .then((res) => {
        if (cancelled) return;
        const opts = res.data
          .map((u) => {
            const name = u.userName ?? u.username ?? '';
            if (!name) return null;
            const label = u.fullName ? `${u.fullName} (${name})` : name;
            return { value: name, label };
          })
          .filter((x): x is { value: string; label: string } => x !== null);
        setCreatorOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setCreatorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Memoize API functions to prevent re-creation
  const getStories = useCallback(() => storiesApi.getStories({
    pageIndex: currentPage - 1,
    pageSize: tablePageSize,
    name: searchText || undefined,
    status: statusFilter || undefined,
    categoryId: tagFilter || undefined,
    createdBy: createdByFilter || undefined,
    pinnedIds: pinnedIds.length > 0 ? pinnedIds.join(',') : undefined,
  }), [currentPage, tablePageSize, searchText, statusFilter, tagFilter, createdByFilter, pinnedIds]);

  const getTags = useCallback(() => tagsApi.getTags({ pageIndex: 0, pageSize: 100, status: TAG_STATUS.ACTIVE }), []);

  // Fetch stories
  const {
    data: storiesData,
    loading: storiesLoading,
    refresh: refreshStories,
  } = useApi(getStories, { immediate: true });

  // Tự động fetch khi filter hoặc trang thay đổi
  useEffect(() => {
    refreshStories();
  }, [refreshStories, searchText, statusFilter, tagFilter, createdByFilter, currentPage, tablePageSize, pinnedIds]);

  const pinnedRows = storiesData?.pinned ?? [];

  const togglePin = useCallback((id: string) => {
    setPinnedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [id, ...prev.filter((x) => x !== id)].slice(0, MAX_PINNED_STORIES);
      localStorage.setItem(PINNED_STORY_IDS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Fetch tags for filter
  const { data: tagsData } = useApi(getTags, { immediate: true });

  // Update form categories when tagsData is loaded and editing
  useEffect(() => {
    if (editingStory && tagsData?.data && editingStory.categories) {
      const categoryIds = editingStory.categories?.map(category => {
        const tagById = tagsData.data.find(tag => tag.id === category);
        if (tagById) {
          return category;
        }
        const tagByName = tagsData.data.find(tag => tag.name === category);
        return tagByName?.id;
      }).filter(Boolean) || [];
      
      form.setFieldsValue({
        categories: categoryIds,
      });
    }
  }, [tagsData, editingStory, form]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback(() => {
    setCurrentPage(1);
  }, []);


  const handleReset = useCallback(() => {
    setSearchText('');
    setStatusFilter('');
    setTagFilter('');
    setCreatedByFilter('');
    setCurrentPage(1);
    setTablePageSize(PAGE_SIZE);
    form.resetFields();
    refreshStories();
  }, [form, refreshStories]);

  const handleCreate = () => {
    setEditingStory(null);
    form.resetFields();
    form.setFieldsValue({
      categories: [],
      coverUrl: undefined,
      ongoing: true,
    });
    setSelectedImageFile(null);
    setImageUrl(undefined);
    setIsModalVisible(true);
  };

  const handleEdit = (story: Story) => {
    setEditingStory(story);
    
    // story.categories is array of category IDs (based on how it's rendered in table: tag.id === category)
    // Use categories directly as they are already IDs
    // useEffect will ensure form is updated if tagsData hasn't loaded when handleEdit is called
    
    form.setFieldsValue({
      title: story.title,
      description: story.description,
      coverUrl: story.coverUrl,
      categories: story.categories || [],
      status: story.status,
      gender: story.gender || GENDER.UNISEX,
      ongoing: story.ongoing || false,
      publishFrequency: story.publishFrequency || undefined,
    });
    setSelectedImageFile(null); // Reset file, will display URL instead
    setImageUrl(story.coverUrl);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    const result = await executeAsync(
      async () => {
        await storiesApi.deleteStory(id);
      },
      { successMessage: 'Story deleted successfully!' }
    );
    if (result === null) return;
    setPinnedIds((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      localStorage.setItem(PINNED_STORY_IDS_KEY, JSON.stringify(next));
      return next;
    });
    refreshStories();
  };

  const handlePublish = async (id: string) => {
    await executeAsync(
      async () => {
        await storiesApi.updateStory(id, { status: STORY_STATUS.PUBLISHED });
      },
      { successMessage: 'Story published successfully!' }
    );
    refreshStories();
  };

  const handleRevertToDraft = async (id: string) => {
    await executeAsync(
      async () => {
        await storiesApi.updateStory(id, { status: STORY_STATUS.DRAFT });
      },
      { successMessage: 'Story reverted to draft successfully!' }
    );
    refreshStories();
  }; 

  const handleSubmit = async (values: any) => {
    // Check if publishFrequency changed and story has scheduled episodes
    if (editingStory) {
      const oldFreq = editingStory.publishFrequency || null;
      const newFreq = values.publishFrequency ?? null;
      const frequencyChanged = (newFreq || null) !== (oldFreq || null);

      if (frequencyChanged && newFreq && newFreq > 0) {
        // Fetch preview before submitting
        setPreviewLoading(true);
        try {
          const previewResponse = await storiesApi.getSchedulePreview(editingStory.id, newFreq);
          const entries = previewResponse.data?.entries || [];
          if (entries.length > 0) {
            setSchedulePreviewEntries(entries);
            setPendingSubmitValues(values);
            setSchedulePreviewVisible(true);
            setPreviewLoading(false);
            return;
          }
        } catch {
          // If preview fails, proceed without confirmation
        }
        setPreviewLoading(false);
      }
    }

    await doSubmit(values);
  };

  const doSubmit = async (values: any) => {
    const imageFile = selectedImageFile;
     
    const requestData: CreateStoryRequest = {
      title: values.title,
      description: values.description,
      status: values.status,
      categories: values.categories,
      gender: values.gender || GENDER.UNISEX,
      ongoing: values.ongoing || false,
      publishFrequency: values.publishFrequency ?? null,
    };
    
    if (!imageFile) {
      requestData.coverUrl = imageUrl || values.coverUrl;
    }

    const operation = editingStory
      ? async () => {
          const response = await storiesApi.updateStory(editingStory.id, requestData as UpdateStoryRequest, imageFile || undefined);
          return 'data' in response ? response.data : response;
        }
      : async () => {
          const response = await storiesApi.createStory(requestData, imageFile || undefined);
          return 'data' in response ? response.data : response;
        };

    const result = await executeAsync(
      operation,
      { 
        successMessage: editingStory 
          ? 'Story updated successfully!' 
          : 'Story created successfully!' 
      }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      form.setFieldsValue({
        categoryIds: [],
        coverUrl: undefined,
      });
      setSelectedImageFile(null);
      setImageUrl(undefined);
      refreshStories();
    }
  };

  const handleSchedulePreviewConfirm = async () => {
    setSchedulePreviewVisible(false);
    if (pendingSubmitValues) {
      await doSubmit(pendingSubmitValues);
      setPendingSubmitValues(null);
      setSchedulePreviewEntries([]);
    }
  };

  const handleSchedulePreviewCancel = () => {
    setSchedulePreviewVisible(false);
    setPendingSubmitValues(null);
    setSchedulePreviewEntries([]);
  };

  const columns = [
    {
      title: 'Poster',
      dataIndex: 'coverUrl',
      key: 'coverUrl',
      width: 80,
      render: (url: string) => (
        url ? (
          <Image
            width={60}
            height={40}
            src={url}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            />
        ) : (
          <div style={{ 
            width: 60, 
            height: 40, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#999'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Story) => (
        <Link to={`/stories/${record.id}`}>
          {truncateText(text, 50)}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status === STORY_STATUS.DRAFT && 'Draft'}
          {status === STORY_STATUS.PUBLISHED && 'Published'}
          {status === STORY_STATUS.ARCHIVED && 'Archived'}
        </Tag>
      ),
    },
    {
      title: 'Categories',
      dataIndex: 'categories',  
      key: 'categories',
      render: (categories: string[]) => (
        <Space wrap>
          {categories?.slice(0, 2).map(category => (
            <Tag key={category} color="blue">
              {tagsData?.data.find(tag => tag.id === category)?.name || 'N/A'}
            </Tag>
          ))}
          {categories && categories.length > 2 && <Tag>+{categories.length - 2}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender: string) => (
        <Tag color={gender === GENDER.MALE ? 'blue' : gender === GENDER.FEMALE ? 'pink' : 'default'}>
          {gender === GENDER.MALE ? 'Male' : gender === GENDER.FEMALE ? 'Female' : 'Unisex'}
        </Tag>
      ),
    },
    {
      title: 'Ongoing',
      dataIndex: 'ongoing',
      key: 'ongoing',
      render: (ongoing: boolean) => (
        ongoing ? (
          <Tag color="orange">Ongoing</Tag>
        ) : (
          <Tag color="default">Completed</Tag>
        )
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (text: string) => truncateText(text, 30),
      },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Story) => (
        <Space>
          <Tooltip title={pinnedIds.includes(record.id) ? 'Unpin' : 'Pin to top'}>
            <Button
              type="text"
              icon={<PushpinOutlined />}
              onClick={() => togglePin(record.id)}
              style={{ color: pinnedIds.includes(record.id) ? '#1890ff' : undefined }}
              aria-label={pinnedIds.includes(record.id) ? 'Unpin' : 'Pin'}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/stories/${record.id}`)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          {record.status === STORY_STATUS.DRAFT && (
            <Popconfirm
              title="Publish this story?"
              onConfirm={() => handlePublish(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                title="Publish"
              />
            </Popconfirm>
          )}
          {record.status === STORY_STATUS.PUBLISHED && (
            <Popconfirm
              title="Revert story to draft?"
              description="This will change the story status from Published to Draft."
              onConfirm={() => handleRevertToDraft(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<RollbackOutlined />}
                title="Revert to Draft"
              />
            </Popconfirm>
          )}
          <Popconfirm
            title="Are you sure you want to delete this story?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header Section */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Stories
          </Title>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
          style={{
            background: '#1890ff',
            borderRadius: '6px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Create
        </Button>
      </div>

      {/* Filter Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Title
            </label>
            <Search
              placeholder="Search by title..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Status
            </label>
            <Select
              placeholder="Select status"
              allowClear
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value || '');
                handleFilterChange();
              }}
              style={{ width: '100%' }}
            >
              <Option value={STORY_STATUS.DRAFT}>Draft</Option>
              <Option value={STORY_STATUS.PUBLISHED}>Published</Option>
              <Option value={STORY_STATUS.ARCHIVED}>Archived</Option>
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Category
            </label>
            <Select
              placeholder="Select category"
              allowClear
              value={tagFilter}
              onChange={(value) => {
                setTagFilter(value || '');
                handleFilterChange();
              }}
              style={{ width: '100%' }}
            >
              {tagsData?.data.map(tag => (
                <Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Created by
            </label>
            <Select
              allowClear
              showSearch
              placeholder="Filter by creator..."
              optionFilterProp="label"
              value={createdByFilter || undefined}
              onChange={(v) => {
                setCreatedByFilter(v ?? '');
                handleFilterChange();
              }}
              options={creatorOptions}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleReset}
            style={{
              borderRadius: '6px',
              height: '36px',
              fontSize: '14px',
            }}
          >
            Reset
          </Button>
        </div>
      </Card>

      {/* Table Card */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Table
          columns={columns}
          dataSource={[...pinnedRows, ...(storiesData?.data ?? [])]}
          rowKey="id"
          loading={storiesLoading}
          pagination={{
            current: currentPage,
            total: storiesData?.totalRecord || 0,
            pageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS.map(String),
            onChange: (page) => {
              setCurrentPage(page);
            },
            onShowSizeChange: (_page, size) => {
              setTablePageSize(size);
              setCurrentPage(1);
            },
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} stories`,
          }}
          scroll={{ x: 1200 }}
          style={{
            fontSize: '14px',
          }}
        />
      </Card>

      <Modal
        title={editingStory ? 'Edit Story' : 'Create New Story'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          form.setFieldsValue({
            categoryIds: [],
            coverUrl: undefined,
          });
          setSelectedImageFile(null);
          setImageUrl(undefined);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter title!' }]}
          >
            <Input placeholder="Enter story title..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description!' }]}
          >
            <TextArea rows={4} placeholder="Enter story description..." />
          </Form.Item>

          <Form.Item
            label="Thumbnail Image"
          >
            <ImageUpload 
              width={300}
              height={200}
              maxSize={10}
              value={selectedImageFile || imageUrl}
              onChange={(value) => {
                if (value instanceof File) {
                  setSelectedImageFile(value);
                  setImageUrl(undefined);
                  form.setFieldsValue({ coverUrl: undefined }); // Clear URL when file is selected
                } else if (value === undefined) {
                  // When deleting image
                  setSelectedImageFile(null);
                  setImageUrl(undefined);
                  form.setFieldsValue({ coverUrl: undefined });
                } else {
                  // When setting URL
                  setSelectedImageFile(null);
                  setImageUrl(value);
                  form.setFieldsValue({ coverUrl: value });
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="categories"
            label="Categories"
          >
            <Select
              mode="multiple"
              placeholder="Select categories..."
              allowClear
              style={{ width: '100%' }}
              loading={!tagsData}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
              showSearch
            >
              {tagsData?.data
                ?.filter(tag => tag.status === TAG_STATUS.ACTIVE)
                .map(tag => (
                  <Option key={tag.id} value={tag.id}>
                    {tag.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="gender"
            label="Gender Target"
            rules={[{ required: true, message: 'Please select gender target!' }]}
            initialValue={GENDER.MALE}
          >
            <Select placeholder="Select gender target...">
              <Option value={GENDER.MALE}>Male</Option>
              <Option value={GENDER.FEMALE}>Female</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="ongoing"
            label="More episodes coming"
            valuePropName="checked"
          >
            <Checkbox>Ongoing</Checkbox>
          </Form.Item>

          <Form.Item
            name="publishFrequency"
            label="Publish Frequency"
            tooltip="Number of episodes to auto-publish per day. Leave empty for manual publishing."
          >
            <Select allowClear placeholder="Manual (default)">
              <Option value={0}>Manual</Option>
              <Option value={1}>1 episode/day</Option>
              <Option value={2}>2 episodes/day</Option>
              <Option value={3}>3 episodes/day</Option>
              <Option value={5}>5 episodes/day</Option>
              <Option value={10}>10 episodes/day</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading || previewLoading}
              >
                {editingStory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Schedule Preview"
        open={schedulePreviewVisible}
        onOk={handleSchedulePreviewConfirm}
        onCancel={handleSchedulePreviewCancel}
        okText="Apply"
        cancelText="Cancel"
        confirmLoading={asyncLoading}
      >
        <p style={{ marginBottom: 16 }}>
          Changing publish frequency to <strong>{pendingSubmitValues?.publishFrequency} ep/day</strong>.
          {editingStory?.publishFrequency && (
            <> (was {editingStory.publishFrequency} ep/day)</>
          )}
        </p>
        {schedulePreviewEntries.length > 0 ? (
          <>
            <p>New schedule for <strong>{schedulePreviewEntries.length}</strong> episode(s):</p>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <Timeline
                items={Object.entries(
                  schedulePreviewEntries.reduce<Record<string, SchedulePreviewEntry[]>>((acc, entry) => {
                    const date = new Date(entry.publishedAt).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    });
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(entry);
                    return acc;
                  }, {})
                ).map(([date, entries]) => ({
                  children: (
                    <div>
                      <strong>{date}</strong>: Ep {entries.map(e => e.chap).join(', ')} ({entries.length} ep)
                      {entries.some(e => e.action === 'publish_now') && (
                        <Tag color="green" style={{ marginLeft: 8 }}>Publish now</Tag>
                      )}
                    </div>
                  ),
                }))}
              />
            </div>
          </>
        ) : (
          <p>No scheduled episodes will be affected.</p>
        )}
      </Modal>
    </div>
  );
};

export default Stories;

